import {
  CoachingLiveArea,
  CoachingReaction,
  CoachingReactionKind,
} from '@euler/model/coaching-practice';
import { SafeHaptics } from '@euler/utils/haptics';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useEffect } from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { PracticeSession } from './usePracticeSession';

export interface LiveAreaBar extends CoachingLiveArea {
  /** The last change, shown briefly; 0 when it did not move. */
  delta: number;
}

/** A reaction stays this long; a critical error stays. */
const kReactionMs = 3_500;

/** A light touch on phone; the web has none. */
function feel(kind: CoachingReactionKind) {
  if (Platform.OS === 'web') return;
  if (kind === CoachingReactionKind.Slip) SafeHaptics.selection();
  else if (kind === CoachingReactionKind.CriticalError)
    SafeHaptics.notificationError();
  else SafeHaptics.notification();
}

/**
 * The live coaching around the dialogue: the areas observed so far, the
 * reaction of the moment, and whether a critical error capped the stars.
 */
export function useCoachingEdge(
  session: PracticeSession,
  initial: CoachingLiveArea[],
) {
  const edge = useCreation(
    () => ({
      areas$: new BehaviorSubject<LiveAreaBar[]>(
        initial.map(x => ({ ...x, delta: 0 })),
      ),
      reaction$: new BehaviorSubject<CoachingReaction | null>(null),
      capped$: new BehaviorSubject(false),
    }),
    [initial],
  );
  useEffect(() => {
    let clear: ReturnType<typeof setTimeout> | null = null;
    const subscription = session.events.subscribe(event => {
      if (event.kind === 'evidence') {
        const bars = new Map(
          edge.areas$.getValue().map(x => [x.area, { ...x, delta: 0 }]),
        );
        for (const change of event.areas) bars.set(change.area, change);
        edge.areas$.next([...bars.values()]);
        if (event.criticalError) edge.capped$.next(true);
      } else if (event.kind === 'reaction') {
        const { reaction } = event;
        if (clear) clearTimeout(clear);
        clear = null;
        edge.reaction$.next(reaction);
        feel(reaction.kind);
        if (reaction.kind === CoachingReactionKind.CriticalError)
          edge.capped$.next(true);
        else clear = setTimeout(() => edge.reaction$.next(null), kReactionMs);
      }
    });
    return () => {
      subscription.unsubscribe();
      if (clear) clearTimeout(clear);
    };
  }, [edge, session]);
  return edge;
}

export type LiveCoachingEdge = ReturnType<typeof useCoachingEdge>;
